import * as React from "react";
import { MemoryRouter, Routes, Route, Navigate } from "react-router-dom";
import MainLayout from "./layouts/main-layout/MainLayout";
import Homepage from "./pages/homepage/Homepage";
import Settings from "./pages/settings/Settings";
import Setup from "./pages/setup/Setup";
import Discussions from "./pages/discussions/Discussions";
import Tasks from "./pages/tasks/Tasks";
import WorkflowStatus from "./pages/workflow-status/WorkflowStatus";
import AccountMappings from "./pages/account-mappings/AccountMappings";
import IncomingChanges from "./pages/incoming-changes/IncomingChanges";
import Review from "./pages/review/Review";
import Documents from "./pages/documents/Documents";
import LoadingPage from "./pages/loading-page/LoadingPage";
import StandaloneMissingIdError from "./pages/standalone-error/StandaloneMissingIdError";
import { useWorkflowIntelligence } from "./contexts/WorkflowIntelligenceContext";
import { useTrackPageViews } from "./hooks/useTrackPageViews";

/**
 * Mounts inside the Router so `useLocation` works, and emits one App
 * Insights pageView per route change. See `useTrackPageViews` for why
 * MemoryRouter needs this manual wire-up.
 */
const RouteTracker: React.FC = () => {
  useTrackPageViews();
  return null;
};

/**
 * Gate for routes that need a real spreadsheet ID. Redirects to Setup when
 * the workbook isn't linked yet (fresh workbook, or one the user just Unlinked
 * from Settings). Belt-and-braces on top of the sidebar's disabled state —
 * catches URL entry, keyboard nav, event-driven navigation, etc.
 *
 * Setup and Settings do NOT wrap in this guard: Setup is the recovery path,
 * Settings hosts the Unlink control itself.
 */
const RequireSpreadsheet: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { spreadsheetId } = useWorkflowIntelligence();
  if (!spreadsheetId) return <Navigate to="/setup" replace />;
  return <>{children}</>;
};

interface AppProps {
  title: string;
}

const App: React.FC<AppProps> = (props: AppProps) => {
  const { isLoading, error, refreshSpreadsheet, needsSetup, isStandalone, spreadsheetId } = useWorkflowIntelligence();

  // Show loading page while initializing or if there's an error
  if (isLoading || error) {
    return <LoadingPage isLoading={isLoading} error={error} onRetry={refreshSpreadsheet} />;
  }

  // Standalone with no spreadsheet ID in the URL is a hard block — there's
  // no way to reach any usable page. Excel mode has a fallback (fresh
  // workbook → Setup creates the ID on Save); standalone doesn't, because
  // there's no workbook to stamp metadata into.
  if (isStandalone && !spreadsheetId) {
    return <StandaloneMissingIdError />;
  }

  // Determine initial route - redirect to setup if workbook hasn't been configured
  const initialRoute = needsSetup ? "/setup" : "/";

  // Only show the main app once initialization is complete
  return (
    <MemoryRouter initialEntries={[initialRoute]} initialIndex={0}>
      <RouteTracker />
      <Routes>
        <Route path="/" element={<MainLayout />}>
          <Route index element={<RequireSpreadsheet><Homepage title={props.title} /></RequireSpreadsheet>} />
          <Route path="setup" element={<Setup />} />
          <Route path="discussions" element={<RequireSpreadsheet><Discussions /></RequireSpreadsheet>} />
          <Route path="tasks" element={<RequireSpreadsheet><Tasks /></RequireSpreadsheet>} />
          <Route path="incoming-changes" element={<RequireSpreadsheet><IncomingChanges /></RequireSpreadsheet>} />
          <Route path="workflow-status" element={<RequireSpreadsheet><WorkflowStatus /></RequireSpreadsheet>} />
          <Route path="account-mappings" element={<RequireSpreadsheet><AccountMappings /></RequireSpreadsheet>} />
          <Route path="review" element={<RequireSpreadsheet><Review /></RequireSpreadsheet>} />
          <Route path="documents" element={<RequireSpreadsheet><Documents /></RequireSpreadsheet>} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </MemoryRouter>
  );
};

export default App;
